import React        from 'react';
import View          from '@widesk-ui/components/View';
import Button        from '@widesk-ui/components/Button';
import { Modal }     from '@widesk-ui/components/modal/Modal';
import { boxBetween }            from '@/TokamapEditor/helpers/planAnalysisRegion';
import { describeRegion }        from '@/TokamapEditor/helpers/planAnalysisRegion';
import { isClickRatherThanDrag } from '@/TokamapEditor/helpers/planAnalysisRegion';
import { fullView }              from '@/TokamapEditor/helpers/usePlanViewport';
import PlanZoomControls          from '@/TokamapEditor/ux/PlanZoomControls';
import { usePlanViewport }       from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanPoint }        from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanRegion }       from '@models/map/dxfTypes';
import type { PlanSourceResult } from '@models/map/dxfTypes';

type Props = {
	open: boolean;
	source: PlanSourceResult;
	/** En pixels naturels de l'aperçu, `null` = tout le plan. */
	region: PlanRegion | null;
	onApply: (region: PlanRegion | null) => void;
	onClose: () => void;
};

type Mode = 'draw' | 'pan';

const MIDDLE_BUTTON = 1;

const PlanAnalysisRegionModal = ({ open, source, region, onApply, onClose }: Props) => {
	const viewport = usePlanViewport(source, () => fullView(source));
	const [draft, setDraft] = React.useState<PlanRegion | null>(region);
	const [mode, setMode] = React.useState<Mode>('draw');
	// Coin de départ du tracé, en plan ET en écran : la distinction clic/tracé se juge à l'écran.
	const [origin, setOrigin] = React.useState<{ plan: PlanPoint; client: PlanPoint } | null>(null);
	const [tracing, setTracing] = React.useState<PlanRegion | null>(null);

	React.useEffect(() => {
		if (!open) return;

		setDraft(region);
		viewport.setView(fullView(source));
	}, [open]);

	const clampToPlan = (point: PlanPoint): PlanPoint => ({
		x: Math.min(Math.max(point.x, 0), source.width),
		y: Math.min(Math.max(point.y, 0), source.height),
	});

	const onPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
		event.currentTarget.setPointerCapture(event.pointerId);

		if ('pan' === mode || MIDDLE_BUTTON === event.button) {
			viewport.startPan(event);

			return;
		}

		const point = viewport.toPlan(event.clientX, event.clientY);
		if (!point) return;

		const plan = clampToPlan(point);
		setOrigin({ plan, client: { x: event.clientX, y: event.clientY } });
		setTracing({ minX: plan.x, minY: plan.y, maxX: plan.x, maxY: plan.y });
	};

	const onPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
		if (viewport.movePan(event) || !origin) return;

		const point = viewport.toPlan(event.clientX, event.clientY);
		if (point) setTracing(boxBetween(origin.plan, clampToPlan(point)));
	};

	const onPointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
		viewport.endPan();
		if (!origin || !tracing) return;

		const onScreen = boxBetween(origin.client, { x: event.clientX, y: event.clientY });
		if (!isClickRatherThanDrag(onScreen)) setDraft(tracing);

		setOrigin(null);
		setTracing(null);
	};

	const apply = () => {
		onApply(draft);
		onClose();
	};

	// `<Modal open>` de widesk n'est pas piloté après le montage : on ne la monte qu'ouverte.
	if (!open) return null;

	const { view } = viewport;
	const shown = tracing ?? draft;

	return (
		<Modal
			open
			onClose={onClose}
			fullScreen
			title="Zone d’analyse"
			bodyPadding={0}
			footer={(
				<View row gap={8} style={{ justifyContent: 'flex-end' }}>
					<Button disabled={!draft} onClick={() => setDraft(null)}>Tout le plan</Button>
					<Button onClick={onClose}>Annuler</Button>
					<Button type="primary" onClick={apply}>Valider la zone</Button>
				</View>
			)}
		>
			<View style={{ position: 'relative', height: 'calc(100vh - 170px)', minHeight: 360, background: '#fff', overflow: 'hidden' }}>
				<svg
					ref={viewport.svgRef}
					viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
					style={{
						width: '100%',
						height: '100%',
						touchAction: 'none',
						cursor: viewport.panning ? 'grabbing' : 'pan' === mode ? 'grab' : 'crosshair',
					}}
					onWheel={viewport.onWheel}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onAuxClick={event => event.preventDefault()}
				>
					<image href={source.imageDataUri} x={0} y={0} width={source.width} height={source.height} />

					{shown && (
						<>
							<path
								d={`M0,0 H${source.width} V${source.height} H0 Z`
									+ ` M${shown.minX},${shown.minY} H${shown.maxX} V${shown.maxY} H${shown.minX} Z`}
								fillRule="evenodd"
								fill="rgba(0, 0, 0, 0.45)"
								pointerEvents="none"
							/>
							<rect
								x={shown.minX}
								y={shown.minY}
								width={shown.maxX - shown.minX}
								height={shown.maxY - shown.minY}
								fill="none"
								stroke="#1668dc"
								strokeWidth={2}
								vectorEffect="non-scaling-stroke"
								pointerEvents="none"
							/>
						</>
					)}
				</svg>

				<View row gap={4} style={{ position: 'absolute', top: 12, left: 12, background: 'rgba(255,255,255,.95)', padding: 4, borderRadius: 6, border: '1px solid #eee' }}>
					<Button size="small" type={'draw' === mode ? 'primary' : 'default'} onClick={() => setMode('draw')}>
						Tracer la zone
					</Button>
					<Button size="small" type={'pan' === mode ? 'primary' : 'default'} onClick={() => setMode('pan')}>
						Se déplacer
					</Button>
				</View>

				<PlanZoomControls
					zoomPercent={viewport.zoomPercent}
					onZoomIn={viewport.zoomIn}
					onZoomOut={viewport.zoomOut}
					onFit={viewport.fit}
				/>

				<View style={{ position: 'absolute', left: 12, bottom: 12, background: 'rgba(255,255,255,.9)', padding: '6px 10px', borderRadius: 4, border: '1px solid #eee' }}>
					<View size={11} color="#666">
						Molette : zoom · glisser : {'draw' === mode ? 'tracer la zone' : 'déplacer'} · clic molette : déplacer
					</View>
					<View size={12}>{describeRegion(draft, source)}</View>
				</View>
			</View>
		</Modal>
	);
};

export default PlanAnalysisRegionModal;
